import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Button, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, IconButton, Tooltip,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { api } from '../api/client';
import type { PageResponse, UserResponse } from '../api/types';

const ALL_ROLES = ['ADMIN', 'OPERADOR', 'CONSULTA'];

export function UsersPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', fullName: '', roles: ['CONSULTA'] as string[] });
  const [err, setErr] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<PageResponse<UserResponse>>('/users', { params: { size: 200 } }).then((r) => r.data),
  });

  const create = useMutation({
    mutationFn: () => api.post('/users', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setOpen(false);
      setForm({ email: '', fullName: '', roles: ['CONSULTA'] });
      setErr(null);
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } } };
      setErr(ax.response?.data?.message ?? 'Error');
    },
  });

  const action = useMutation({
    mutationFn: (vars: { id: number; action: 'lock' | 'unlock' | 'reset-mfa' }) => api.post(`/users/${vars.id}/${vars.action}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const cols: GridColDef<UserResponse>[] = [
    { field: 'email', headerName: 'Email', flex: 1.5 },
    { field: 'fullName', headerName: 'Nombre', flex: 1.2 },
    { field: 'roles', headerName: 'Roles', flex: 1, renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          {p.value.map((r: string) => <Chip key={r} label={r} size="small" />)}
        </Stack>
    )},
    { field: 'status', headerName: 'Status', flex: 0.7,
      renderCell: (p) => <Chip label={p.value} size="small" color={p.value === 'ACTIVE' ? 'success' : p.value === 'LOCKED' ? 'error' : 'default'} /> },
    { field: 'mfaEnrolled', headerName: 'MFA', flex: 0.5, renderCell: (p) => (p.value ? '✓' : '—') },
    { field: 'actions', headerName: 'Acciones', flex: 1, sortable: false, renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Reset MFA"><IconButton size="small" onClick={() => action.mutate({ id: p.row.id, action: 'reset-mfa' })}><LockResetIcon fontSize="small" /></IconButton></Tooltip>
          {p.row.status === 'LOCKED'
            ? <Tooltip title="Desbloquear"><IconButton size="small" onClick={() => action.mutate({ id: p.row.id, action: 'unlock' })}><LockOpenIcon fontSize="small" /></IconButton></Tooltip>
            : <Tooltip title="Bloquear"><IconButton size="small" onClick={() => action.mutate({ id: p.row.id, action: 'lock' })}><LockIcon fontSize="small" /></IconButton></Tooltip>}
        </Stack>
    )},
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Usuarios</Typography>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={() => list.refetch()}><RefreshIcon /></IconButton>
          <Button variant="contained" onClick={() => setOpen(true)}>Nuevo usuario</Button>
        </Stack>
      </Stack>
      <Paper sx={{ height: 600 }}>
        <DataGrid<UserResponse>
          rows={list.data?.content ?? []}
          columns={cols}
          loading={list.isLoading}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo usuario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
            <Alert severity="info">El usuario recibirá un email para definir su contraseña.</Alert>
            <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <TextField label="Nombre completo" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
            <TextField select label="Rol" value={form.roles[0]} onChange={(e) => setForm({ ...form, roles: [e.target.value] })}>
              {ALL_ROLES.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => create.mutate()} disabled={create.isPending}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
